/**
 * DeviceMaintenanceHistory.js
 *
 * Device-centric Maintenance & Complaint History View
 * Shows all maintenance activities and complaints for accessible devices
 *
 * Features:
 * - View combined maintenance and complaint history across all accessible devices
 * - Quick filter by activity type (All / Maintenance Only / Complaints Only)
 * - Search by device ID, device name, department, complaint ID, technician name, description
 * - Navigation from Device View / Device List seeds search with device_identifier (same as Alerts/Complaints)
 * - View summary statistics (total activities, maintenance count, complaint count, total hours)
 * - Expandable row details for full activity information (logged/created date)
 * - List/sort/filter by updated date (recent activity)
 * - Show technician name (who performed the maintenance)
 * - Pagination with configurable items per page
 * - Clean, minimal table design matching MyWorkLogs component
 *
 * Props:
 * - token: JWT authentication token
 * - user: Current user object
 * - initialSearchFilter: optional device_identifier (or search text) from Device View navigation
 * - onSearchFilterChange: callback when search is cleared/changed (keeps App navigation state in sync)
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { DeviceIDBadge, ComplaintIDBadge, SeverityBadge } from '../../../shared/components/common/Badges';
import StatsCard from '../../../shared/components/common/StatsCard';
import Pagination from '../../../shared/components/common/Pagination';
import ClampedDetailsText from '../../../shared/components/common/ClampedDetailsText';
import { toast } from 'react-toastify';
import ExportButton from '../../../shared/components/modals/ExportButton';
import ExportColumnConfigurationModal from '../../../shared/components/modals/ExportColumnConfigurationModal';
import FilterHelpModal from '../../../shared/components/modals/FilterHelpModal';
import MoreFiltersButton from '../../../shared/components/common/MoreFiltersButton';
import { exportFilteredMaintenanceHistory } from '../../../utils/exportUtils';
import {
  ADVANCED_FILTER_HINTS,
  FilterTitleHint,
} from '../../../shared/components/common/advancedFilterDrawerHints';
import { formatHoursSpent } from '../../../utils/numberUtils';
import { getMaintenanceTypeLabel } from '../utils/maintenanceUtils';
import {
  createMaintenanceExportColumnsState,
  getSelectedExportColumnHeaders,
} from '../constants/maintenanceExportColumns';
import {
  PDF_EXPORT_MAX_COLUMNS,
  validatePdfExportColumnCount,
} from '../../../constants/exportColumnPolicy';
import './DeviceMaintenanceHistory.css';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { MAINTENANCE_LIST_DEFAULT_LOOKBACK_DAYS } from '../../../shared/utils/listDefaults';

const toDateString = (date) => date.toISOString().split('T')[0];

const daysAgoDateString = (days) => {
  const today = new Date();
  return toDateString(new Date(today.getTime() - days * 24 * 60 * 60 * 1000));
};

const DeviceMaintenanceHistory = ({ token, user, initialSearchFilter = null, onSearchFilterChange = null }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Seed search from Device View / Device List navigation (device_identifier), same as Complaints
  const navSearch =
    initialSearchFilter !== null && initialSearchFilter !== undefined
      ? String(initialSearchFilter).trim()
      : '';

  // Unified filter state - consolidates all filter fields into a single object
  const [filters, setFilters] = useState({
    searchTerm: navSearch,
    dateFrom: '',
    dateTo: '',
    departmentFilters: [], // Multi-select
    maintenanceTypeFilters: [], // Multi-select
    complaintStatusFilters: [], // Multi-select
    severityFilters: [], // Multi-select
    typeFilter: '' // Single select: '', 'maintenance', 'complaint'
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Sorting state
  const [sorting, setSorting] = useState({
    sort_by: 'date',
    sort_direction: 'desc'
  });

  // Data
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [stats, setStats] = useState({
    total_count: 0,
    maintenance_count: 0,
    complaint_count: 0,
    total_hours: 0,
    maintenance_hours: 0,
    complaint_hours: 0
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [showPdfColumnsConfig, setShowPdfColumnsConfig] = useState(false);
  const [showAdvancedPanel, setShowAdvancedPanel] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    dateRange: true,
    department: true,
    type: true,
    maintenanceType: true,
    complaintFilters: true
  });
  const [drawerFilters, setDrawerFilters] = useState({
    dateFrom: '',
    dateTo: '',
    departmentFilters: [],
    maintenanceTypeFilters: [],
    complaintStatusFilters: [],
    severityFilters: [],
    typeFilter: ''
  });
  const [pdfColumns, setPdfColumns] = useState(() => createMaintenanceExportColumnsState('excel'));
  const [isExportingHistory, setIsExportingHistory] = useState(false);
  const [showFilterHelp, setShowFilterHelp] = useState(false);

  // Search input state - separate from filter state to prevent focus loss
  // searchInputValue is updated immediately as user types
  // filters.searchTerm is updated with debounce (500ms delay)
  const [searchInputValue, setSearchInputValue] = useState(navSearch);
  const searchDebounceTimerRef = useRef(null);
  const appliedNavSearchRef = useRef(navSearch);

  // Track initial mount to prevent duplicate API calls
  const isInitialMountRef = useRef(true);

  // Re-apply navigation search when parent passes a new initialSearchFilter (e.g. another device)
  useEffect(() => {
    const nextSearch =
      initialSearchFilter !== null && initialSearchFilter !== undefined
        ? String(initialSearchFilter).trim()
        : '';
    if (nextSearch === appliedNavSearchRef.current) {
      return;
    }
    appliedNavSearchRef.current = nextSearch;
    setSearchInputValue(nextSearch);
    setFilters((prev) => ({ ...prev, searchTerm: nextSearch }));
  }, [initialSearchFilter]);

  useEffect(() => {
    fetchAllDepartments();
  }, [token]);

  // Create stable dependency strings to prevent duplicate fetches
  // This prevents the entire filters object reference from triggering useEffect
  // Only changes when actual filter VALUES change, not when object reference changes
  const filterKey = `${filters.searchTerm}|${filters.dateFrom}|${filters.dateTo}|${filters.departmentFilters.join(',')}|${filters.maintenanceTypeFilters.join(',')}|${filters.complaintStatusFilters.join(',')}|${filters.severityFilters.join(',')}|${filters.typeFilter}`;
  const sortKey = `${sorting.sort_by}|${sorting.sort_direction}`;

  // Refetch when actual filter or sorting values change (not object reference)
  // When any filter changes, reset pagination to page 1
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchMaintenanceHistory();
    }
  }, [filterKey, sortKey]);

  // Fetch when pagination changes (same filters)
  // Skip on initial mount to avoid duplicate API call (filterKey useEffect already calls it)
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return; // Skip initial mount
    }
    fetchMaintenanceHistory();
  }, [currentPage, perPage]);

  const updateSearchTerm = (value) => {
    const next = value || '';
    setFilters((prevFilters) => ({
      ...prevFilters,
      searchTerm: next
    }));
    appliedNavSearchRef.current = next;
    if (onSearchFilterChange) {
      onSearchFilterChange(next ? next : null);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // DEBOUNCE SEARCH INPUT - Prevent focus loss while typing
  // ═══════════════════════════════════════════════════════════════════
  // How it works:
  // 1. User types → setSearchInputValue (instant, keeps focus)
  // 2. Debounce waits 500ms after last keystroke
  // 3. Updates filters.searchTerm → triggers API call
  // 4. Component re-renders but input stays focused (it's bound to searchInputValue, not filters.searchTerm)
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Clear any pending debounce timer
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    // If user cleared the search, update immediately
    if (searchInputValue === '') {
      if (filters.searchTerm !== '') {
        updateSearchTerm('');
      }
      return;
    }

    // Skip debounce when value already matches (e.g. seeded from navigation)
    if (searchInputValue === filters.searchTerm) {
      return;
    }

    // Set new debounce timer for non-empty searches
    searchDebounceTimerRef.current = setTimeout(() => {
      updateSearchTerm(searchInputValue);
    }, 500); // 500ms debounce delay

    // Cleanup on unmount
    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchInputValue]);

  // Sync searchInputValue when filters.searchTerm is cleared externally (e.g., "All" button)
  useEffect(() => {
    if (filters.searchTerm === '' && searchInputValue !== '') {
      setSearchInputValue('');
    }
  }, [filters.searchTerm]);

  // ═══════════════════════════════════════════════════════════════════
  // PREVENT BODY SCROLL WHEN ADVANCED FILTER PANEL IS OPEN
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (showAdvancedPanel) {
      // Disable scrolling on body when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to ensure scroll is re-enabled if component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showAdvancedPanel]);

  // ═══════════════════════════════════════════════════════════════════
  // AUTO-SELECT TYPE FILTER when type-specific filters are selected
  // SIMPLIFIED APPROACH: Only auto-select, never auto-clear
  // ═══════════════════════════════════════════════════════════════════
  // HOW IT WORKS:
  // 1. User clicks "PPM" → maintenanceTypeFilters = ['PPM']
  //    → useEffect auto-selects typeFilter = 'maintenance'
  //    → API called with type=maintenance&maintenance_types=PPM
  //
  // 2. User clicks "PPM" again to deselect
  //    → maintenanceTypeFilters = []
  //    → typeFilter stays 'maintenance' (NOT auto-cleared)
  //    → API called with type=maintenance (all maintenance records)
  //
  // 3. User clicks "🔧 Maintenance Only" button to clear type filter
  //    → typeFilter = ''
  //    → Now showing all records (both complaints and maintenance)
  //
  // WHY? Simpler logic = no bugs. User has full control.
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    // If maintenance type filters exist, auto-set typeFilter to 'maintenance'
    if (filters.maintenanceTypeFilters.length > 0) {
      if (filters.typeFilter !== 'maintenance') {
        setFilters(prevFilters => ({
          ...prevFilters,
          typeFilter: 'maintenance'
        }));
      }
    }
  }, [filters.maintenanceTypeFilters]);

  // If complaint status or severity filters exist, auto-set typeFilter to 'complaint'
  useEffect(() => {
    if (filters.complaintStatusFilters.length > 0 || filters.severityFilters.length > 0) {
      if (filters.typeFilter !== 'complaint') {
        setFilters(prevFilters => ({
          ...prevFilters,
          typeFilter: 'complaint'
        }));
      }
    }
  }, [filters.complaintStatusFilters, filters.severityFilters]);

  // ═══════════════════════════════════════════════════════════════════
  // ENFORCE MUTUAL EXCLUSIVITY: Main filters cleanup
  // Maintenance type vs complaint filters (status and/or severity)
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (
      filters.maintenanceTypeFilters.length > 0
      && (filters.complaintStatusFilters.length > 0 || filters.severityFilters.length > 0)
    ) {
      setFilters(prevFilters => ({
        ...prevFilters,
        complaintStatusFilters: [],
        severityFilters: []
      }));
    }
  }, [filters.maintenanceTypeFilters, filters.complaintStatusFilters, filters.severityFilters]);

  const fetchAllDepartments = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.DEPARTMENTS.LIST, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAvailableDepartments(response.data.departments || []);
    } catch (err) {
      console.error('Error fetching departments:', err);
      setAvailableDepartments([]);
    }
  };

  const fetchMaintenanceHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      let params = {
        page: currentPage,
        limit: perPage
      };

      // Add search filter (includes device_identifier when navigated from Device View)
      if (filters.searchTerm.trim()) {
        params.search = filters.searchTerm;
      }

      // Add date range filters
      if (filters.dateFrom) {
        params.date_from = filters.dateFrom;
      }
      if (filters.dateTo) {
        params.date_to = filters.dateTo;
      }

      // Add department filters
      if (filters.departmentFilters.length > 0) {
        params.departments = filters.departmentFilters.join(',');
      }

      // Add maintenance type filters
      if (filters.maintenanceTypeFilters.length > 0) {
        params.maintenance_types = filters.maintenanceTypeFilters.join(',');
      }

      // Add complaint status filters
      if (filters.complaintStatusFilters.length > 0) {
        params.complaint_statuses = filters.complaintStatusFilters.join(',');
      }

      // Add complaint severity filters
      if (filters.severityFilters.length > 0) {
        params.severities = filters.severityFilters.join(',');
      }

      // Add type filter
      if (filters.typeFilter) {
        params.type = filters.typeFilter;
      }

      // Add sorting
      if (sorting && sorting.sort_by) {
        params.sort_by = sorting.sort_by;
        params.sort_direction = sorting.sort_direction;
      }

      const response = await axios.get(API_ENDPOINTS.MAINTENANCE.HISTORY, {
        params,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setStats({
        total_count: response.data.total_count,
        maintenance_count: response.data.maintenance_count,
        complaint_count: response.data.complaint_count,
        total_hours: response.data.total_hours,
        maintenance_hours: response.data.maintenance_hours || 0,
        complaint_hours: response.data.complaint_hours || 0
      });

      // Set pagination metadata from server response
      setTotalRecords(response.data.total_count || 0);
      setTotalPages(Math.ceil((response.data.total_count || 0) / perPage));

      // Process history items
      const processedHistory = (response.data.history || []).map(item => {
        if (item.type === 'maintenance') {
          return {
            id: item.id,
            type: 'maintenance',
            title: `${item.maintenance_type} - ${item.device_name}`,
            date: item.updated_at,
            logDate: item.created_at,
            hours_spent: item.hours_spent || 0,
            status: 'completed',
            department: item.department,
            department_icon: item.department_icon || '🏢',
            device_id: item.device_identifier,
            device_name: item.device_name,
            device_db_id: item.device_id,
            maintenance_type: item.maintenance_type,
            performed_by_name: item.performed_by_name,
            performed_by_employee_id: item.performed_by_employee_id,
            description: item.description,
            maintenance_date: item.maintenance_date,
            due_date: item.due_date,
            next_due_date: item.next_due_date,
            icon: getMaintenanceIcon(item.maintenance_type)
          };
        } else {
          // Complaint
          return {
            id: item.id,
            type: 'complaint',
            title: item.title,
            date: item.updated_at,
            logDate: item.created_at,
            hours_spent: item.hours_spent || 0,
            status: item.status,
            severity: item.severity || 'minor',
            department: item.department,
            department_icon: item.department_icon || '🏢',
            device_id: item.device_identifier,
            device_name: item.device_name,
            device_db_id: item.device_id,
            complaint_id: item.complaint_identifier,
            problem_details: item.problem_details,
            closing_remarks: item.closing_remarks,
            spare_parts_required: item.spare_parts_required,
            performed_by_name: item.performed_by_name, // Backend provides full_name for assigned engineer
            created_at: item.created_at,
            closed_at: item.closed_at,
            icon: '💬'
          };
        }
      });

      setHistory(processedHistory);
    } catch (err) {
      console.error('Error fetching maintenance history:', err);
      setError('Failed to load maintenance history');
    } finally {
      setLoading(false);
    }
  };

  const getMaintenanceIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'ppm':
        return '🔧';
      case 'calibration':
        return '⚙️';
      case 'compliance':
        return '📋';
      default:
        return '📝';
    }
  };

  /**
   * Handle column sort change - toggle direction if same column, or set new column with asc
   */
  const handleSortChange = (column) => {
    setSorting(prev => {
      // If same column clicked, toggle direction; otherwise set new column with asc
      if (prev.sort_by === column) {
        return {
          ...prev,
          sort_direction: prev.sort_direction === 'asc' ? 'desc' : 'asc'
        };
      } else {
        return {
          sort_by: column,
          sort_direction: 'asc'
        };
      }
    });
  };

  /**
   * Get sort indicator for column header (▲ for asc, ▼ for desc)
   */
  const getSortIndicator = (column) => {
    if (sorting.sort_by !== column) return '';
    return sorting.sort_direction === 'asc' ? ' ▲' : ' ▼';
  };

  // IMPORTANT: workTypeFilter is ONLY for display, not for stats calculation
  // Stats must be calculated from the full server-filtered history (before workTypeFilter)
  // to show accurate counts regardless of which tab is selected

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return { date: 'N/A', time: '' };
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const calculateResolutionTime = (createdAt, resolvedAt) => {
    if (!createdAt || !resolvedAt) return 'N/A';

    const created = new Date(createdAt);
    const resolved = new Date(resolvedAt);
    const diffMs = resolved - created;

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ${hours}h`;
    }
    return `${hours}h`;
  };

  const getMaintenanceTimeliness = (maintenanceDate, dueDate) => {
    if (!maintenanceDate || !dueDate) return { isOnTime: null, daysLate: 0 };

    const completed = new Date(maintenanceDate);
    const due = new Date(dueDate);
    const isOnTime = completed <= due;
    const diffMs = completed - due;
    const daysLate = isOnTime ? 0 : Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    return {
      isOnTime,
      daysLate
    };
  };

  const getTotalCounts = () => {
    // Get total counts from backend stats (for reference/context)
    return {
      complaints: stats.complaint_count,
      maintenance: stats.maintenance_count
    };
  };

  const getFilteredCounts = () => {
    // Stats from server now include filtered results, no need for client-side calculation
    return {
      complaints: stats.complaint_count,
      maintenance: stats.maintenance_count
    };
  };

  const getTotalHours = () => {
    // Backend now returns accurate breakdown by type
    // Hours include all filtered results (search, date range, departments, etc.)
    const totalHours = stats.total_hours || 0;
    const maintenanceHours = stats.maintenance_hours || 0;
    const complaintHours = stats.complaint_hours || 0;

    return { totalHours, complaintHours, maintenanceHours };
  };

  // Structured filters only — free-text search is not an Active Filter
  const hasActiveFilters = !!(filters.dateFrom || filters.dateTo || filters.departmentFilters.length > 0 ||
                               filters.maintenanceTypeFilters.length > 0 || filters.complaintStatusFilters.length > 0 ||
                               filters.severityFilters.length > 0 || filters.typeFilter);

  const totalCounts = getTotalCounts();
  const filteredCounts = getFilteredCounts();
  // Use filtered counts for display, but fallback to total counts if no filters
  const counts = hasActiveFilters ? filteredCounts : totalCounts;
  const hoursData = getTotalHours();

  // Server-side filtering already applied - no client-side type filtering needed
  // All filtering is done via API parameters
  const filteredHistory = history;

  const buildMaintenanceExportParams = () => {
    const params = {};
    if (filters.searchTerm.trim()) {
      params.search = filters.searchTerm;
    }
    if (filters.dateFrom) {
      params.date_from = filters.dateFrom;
    }
    if (filters.dateTo) {
      params.date_to = filters.dateTo;
    }
    if (filters.departmentFilters.length > 0) {
      params.departments = filters.departmentFilters.join(',');
    }
    if (filters.maintenanceTypeFilters.length > 0) {
      params.maintenance_types = filters.maintenanceTypeFilters.join(',');
    }
    if (filters.complaintStatusFilters.length > 0) {
      params.complaint_statuses = filters.complaintStatusFilters.join(',');
    }
    if (filters.severityFilters.length > 0) {
      params.severities = filters.severityFilters.join(',');
    }
    if (filters.typeFilter) {
      params.type = filters.typeFilter;
    }
    if (sorting?.sort_by) {
      params.sort_by = sorting.sort_by;
      params.sort_direction = sorting.sort_direction;
    }
    return params;
  };

  const exportToPDF = async () => {
    if (!token) return;
    const selected = getSelectedExportColumnHeaders(pdfColumns);
    if (selected.length === 0) {
      toast.error('Select at least one column to export');
      return;
    }
    const pdfCheck = validatePdfExportColumnCount(selected.length);
    if (!pdfCheck.ok) {
      toast.error(pdfCheck.message);
      return;
    }
    setIsExportingHistory(true);
    try {
      const { exportedRowCount } = await exportFilteredMaintenanceHistory(
        buildMaintenanceExportParams(),
        'pdf',
        token,
        selected
      );
      const count = exportedRowCount ?? stats.total_count;
      toast.success(
        count > 0
          ? `Exported ${count} record${count !== 1 ? 's' : ''} to PDF`
          : 'Export complete'
      );
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error(error.message || 'Failed to export PDF');
    } finally {
      setIsExportingHistory(false);
    }
  };

  const exportToExcel = async () => {
    if (!token) return;
    const selected = getSelectedExportColumnHeaders(pdfColumns);
    if (selected.length === 0) {
      toast.error('Select at least one column to export');
      return;
    }
    setIsExportingHistory(true);
    try {
      const { exportedRowCount } = await exportFilteredMaintenanceHistory(
        buildMaintenanceExportParams(),
        'excel',
        token,
        selected
      );
      const count = exportedRowCount ?? stats.total_count;
      toast.success(
        count > 0
          ? `Exported ${count} record${count !== 1 ? 's' : ''} to Excel`
          : 'Export complete'
      );
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error(error.message || 'Failed to export Excel');
    } finally {
      setIsExportingHistory(false);
    }
  };

  if (error && !loading && history.length === 0) {
    return (
      <div className="work-logs-container">
        <div className="work-logs-error">
          <div className="error-icon">⚠️</div>
          <p>{error}</p>
          <button onClick={fetchMaintenanceHistory} className="btn-retry">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Results already sorted and paginated by server - just apply work type filter
  const paginatedHistory = filteredHistory;

  const clearAllFilters = () => {
    setSearchInputValue('');
    setFilters({
      searchTerm: '',
      dateFrom: '',
      dateTo: '',
      departmentFilters: [],
      maintenanceTypeFilters: [],
      complaintStatusFilters: [],
      severityFilters: [],
      typeFilter: ''
    });
    appliedNavSearchRef.current = '';
    if (onSearchFilterChange) {
      onSearchFilterChange(null);
    }
  };

  const isLastNDaysActive = (days) => {
    if (!filters.dateFrom || !filters.dateTo) return false;
    return (
      filters.dateFrom === daysAgoDateString(days)
      && filters.dateTo === toDateString(new Date())
    );
  };

  const toggleLastNDays = (days) => {
    if (isLastNDaysActive(days)) {
      setFilters({ ...filters, dateFrom: '', dateTo: '' });
      return;
    }
    setFilters({
      ...filters,
      dateFrom: daysAgoDateString(days),
      dateTo: toDateString(new Date()),
    });
  };

  const isServerDateWindowActive = !filters.dateFrom && !filters.dateTo;

  const hasComplaintFilters = filters.complaintStatusFilters.length > 0 || filters.severityFilters.length > 0;
  const drawerHasComplaintFilters = drawerFilters.complaintStatusFilters.length > 0
    || drawerFilters.severityFilters.length > 0;
  const drawerHasMaintenanceTypeFilters = drawerFilters.maintenanceTypeFilters.length > 0;
  const isDepartmentFilterRestricted = Boolean(
    user
    && ['coordinator', 'custodian', 'technician'].includes(user.role)
    && user.departments
    && user.departments.length === 1
  );

  return (
    <div className="work-logs-container">
      {/* Title / description — matches Complaints & Devices */}
      <div className="work-logs-description-section">
        <div className="title-with-button">
          <h2 className="work-logs-title">
            🔧 Device Maintenance History
          </h2>
          <button onClick={fetchMaintenanceHistory} className="btn-refresh" title="Refresh history" style={{ padding: '0.5rem', fontSize: '1.2rem', border: 'none', background: 'none', cursor: 'pointer', lineHeight: 1 }}>
            🔄
          </button>
        </div>
        <p className="work-logs-subtitle">
          View all maintenance activities and complaints across your devices
        </p>
      </div>

      {isServerDateWindowActive && (
        <div className="list-default-window-note" role="note">
          <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
          <span>
            Showing history from the{' '}
            <strong>last {MAINTENANCE_LIST_DEFAULT_LOOKBACK_DAYS} days</strong> by default.
            Use Last 7d / 15d / 30d or Advanced Filters for a different range.
          </span>
        </div>
      )}

      {/* Stats below the horizontal bar */}
      <div className="work-logs-header">
        <div className="work-logs-stats">
          <StatsCard
            icon="💬"
            number={`${counts.complaints}${hasActiveFilters && totalCounts.complaints !== counts.complaints ? ` of ${totalCounts.complaints}` : ''}`}
            label="Complaints"
            className="complaint"
          />
          <StatsCard
            icon="🔧"
            number={`${counts.maintenance}${hasActiveFilters && totalCounts.maintenance !== counts.maintenance ? ` of ${totalCounts.maintenance}` : ''}`}
            label="Maintenance"
            className="maintenance"
          />
          <StatsCard
            icon="⏱️"
            number={formatHoursSpent(hoursData.totalHours, { zeroDisplay: '0:00' })}
            label="Total Hours"
            className="hours"
            aside={(
              <>
                <div className="stats-card-hours-aside-row">
                  <span aria-hidden="true">💬</span>
                  <strong>{formatHoursSpent(hoursData.complaintHours, { zeroDisplay: '0:00' })}</strong>
                </div>
                <div className="stats-card-hours-aside-row">
                  <span aria-hidden="true">🔧</span>
                  <strong>{formatHoursSpent(hoursData.maintenanceHours, { zeroDisplay: '0:00' })}</strong>
                </div>
              </>
            )}
          />
        </div>
      </div>

      {/* Export Columns Configuration Modal */}
      <ExportColumnConfigurationModal
        isOpen={showPdfColumnsConfig}
        onClose={() => setShowPdfColumnsConfig(false)}
        title="Configure Export Columns"
        description="A minimal operational set is pre-selected. Add or remove columns as needed. Excel supports any number of columns; PDF is limited for readable landscape layout."
        columns={pdfColumns}
        onColumnsChange={setPdfColumns}
        pdfMaxColumns={PDF_EXPORT_MAX_COLUMNS}
      />

      {/* Search */}
      <div className="search-section">
        <div className="search-box-wrapper">
          <span className="search-icon-lens">🔍</span>
          <input
            type="text"
            placeholder="Search by complaint ID, device ID, device name, department, technician, or description..."
            value={searchInputValue}
            onChange={(e) => setSearchInputValue(e.target.value)}
            className="search-input-clean"
          />
          {searchInputValue && (
            <button
              className="clear-search-btn-inline"
              onClick={() => {
                setSearchInputValue(''); // Clear input immediately
              }}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* QUICK FILTERS (PRESETS) */}
      <div className="quick-filters">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.75rem' }}>
          <div className="quick-filters-title">
            <svg className="filters-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            Quick Filters
          </div>
        </div>
        <div className="preset-buttons">
          <button
            className={`preset-btn ${!hasActiveFilters && !filters.searchTerm ? 'active' : ''}`}
            onClick={clearAllFilters}
            title={`Clear quick filters (default last ${MAINTENANCE_LIST_DEFAULT_LOOKBACK_DAYS} days still applies)`}
          >
            All
          </button>
          <button
            className={`preset-btn ${isLastNDaysActive(7) ? 'active' : ''}`}
            onClick={() => toggleLastNDays(7)}
          >
            Last 7d
          </button>
          <button
            className={`preset-btn ${isLastNDaysActive(15) ? 'active' : ''}`}
            onClick={() => toggleLastNDays(15)}
          >
            Last 15d
          </button>
          <button
            className={`preset-btn ${isLastNDaysActive(30) ? 'active' : ''}`}
            onClick={() => toggleLastNDays(30)}
          >
            Last 30d
          </button>
          <button
            className={`preset-btn ${filters.complaintStatusFilters.includes('closed') ? 'active' : ''}`}
            onClick={() => {
              // Toggle: if already applied, remove it; otherwise add it
              if (filters.complaintStatusFilters.includes('closed')) {
                setFilters({ ...filters, complaintStatusFilters: filters.complaintStatusFilters.filter(s => s !== 'closed') });
              } else {
                setFilters({ ...filters, complaintStatusFilters: [...filters.complaintStatusFilters, 'closed'] });
              }
            }}
            disabled={filters.maintenanceTypeFilters.length > 0}
            style={{ opacity: filters.maintenanceTypeFilters.length > 0 ? 0.5 : 1, cursor: filters.maintenanceTypeFilters.length > 0 ? 'not-allowed' : 'pointer' }}
            title={filters.maintenanceTypeFilters.length > 0 ? 'Disabled - Cannot use with Maintenance Type filters' : 'Filter by closed complaints (auto-enables "💬 Complaints Only")'}
          >
            Closed
          </button>
          <button
            className={`preset-btn ${filters.maintenanceTypeFilters.includes('PPM') ? 'active' : ''}`}
            onClick={() => {
              // Toggle: if already applied, remove it; otherwise add it
              if (filters.maintenanceTypeFilters.includes('PPM')) {
                setFilters({ ...filters, maintenanceTypeFilters: filters.maintenanceTypeFilters.filter(t => t !== 'PPM') });
              } else {
                setFilters({ ...filters, maintenanceTypeFilters: [...filters.maintenanceTypeFilters, 'PPM'] });
              }
            }}
            disabled={hasComplaintFilters}
            style={{ opacity: hasComplaintFilters ? 0.5 : 1, cursor: hasComplaintFilters ? 'not-allowed' : 'pointer' }}
            title={hasComplaintFilters ? 'Disabled - Cannot use with Complaint filters' : 'Filter by PPM maintenance (auto-enables "🔧 Maintenance Only")'}
          >
            PPM
          </button>
          <button
            className={`preset-btn ${filters.typeFilter === 'maintenance' ? 'active' : ''}`}
            onClick={() => {
              // Toggle type filter - simple on/off
              if (filters.typeFilter === 'maintenance') {
                setFilters({ ...filters, typeFilter: '' });
              } else {
                setFilters({ ...filters, typeFilter: 'maintenance' });
              }
            }}
            disabled={hasComplaintFilters}
            style={{ opacity: hasComplaintFilters ? 0.5 : 1, cursor: hasComplaintFilters ? 'not-allowed' : 'pointer' }}
            title={hasComplaintFilters ? 'Disabled - Cannot use with Complaint filters' : 'Show only maintenance records. Auto-enabled when selecting Maintenance Type. Click to disable and show all records.'}
          >
            🔧 Maintenance Only
          </button>
          <button
            className={`preset-btn ${filters.typeFilter === 'complaint' ? 'active' : ''}`}
            onClick={() => {
              // Toggle type filter - simple on/off
              if (filters.typeFilter === 'complaint') {
                setFilters({ ...filters, typeFilter: '' });
              } else {
                setFilters({ ...filters, typeFilter: 'complaint' });
              }
            }}
            disabled={filters.maintenanceTypeFilters.length > 0}
            style={{ opacity: filters.maintenanceTypeFilters.length > 0 ? 0.5 : 1, cursor: filters.maintenanceTypeFilters.length > 0 ? 'not-allowed' : 'pointer' }}
            title={filters.maintenanceTypeFilters.length > 0 ? 'Disabled - Cannot use with Maintenance Type filters' : 'Show only complaint records. Auto-enabled when selecting Complaint Status or Severity. Click to disable and show all records.'}
          >
            💬 Complaints Only
          </button>
          <MoreFiltersButton
            isOpen={showAdvancedPanel}
            hasAdditional={[
              filters.departmentFilters.length,
              filters.severityFilters.length,
              filters.maintenanceTypeFilters.length > 0
                && !(filters.maintenanceTypeFilters.length === 1 && filters.maintenanceTypeFilters.includes('PPM')),
              filters.complaintStatusFilters.length > 0
                && !(filters.complaintStatusFilters.length === 1 && filters.complaintStatusFilters.includes('closed')),
              (filters.dateFrom || filters.dateTo)
                && !isLastNDaysActive(7)
                && !isLastNDaysActive(15)
                && !isLastNDaysActive(30),
            ].some(Boolean)}
            onClick={() => {
              if (!showAdvancedPanel) {
                setDrawerFilters({
                  dateFrom: filters.dateFrom,
                  dateTo: filters.dateTo,
                  departmentFilters: filters.departmentFilters,
                  typeFilter: filters.typeFilter,
                  maintenanceTypeFilters: filters.maintenanceTypeFilters,
                  complaintStatusFilters: filters.complaintStatusFilters,
                  severityFilters: filters.severityFilters
                });
              }
              setShowAdvancedPanel(!showAdvancedPanel);
            }}
            onHelpClick={() => setShowFilterHelp(true)}
            helpTitle="Learn how filters work"
          />
        </div>
      </div>

      {/* ACTIVE FILTERS DISPLAY - Only shown when filters are active */}
      {hasActiveFilters && (
        <div className="active-filters">
          <div className="active-filters-header">
            <span className="active-filters-label">Filters Active:</span>
            <button
              className="clear-all-btn"
              onClick={() => {
                setFilters({
                  searchTerm: filters.searchTerm,
                  dateFrom: '',
                  dateTo: '',
                  departmentFilters: [],
                  maintenanceTypeFilters: [],
                  complaintStatusFilters: [],
                  severityFilters: [],
                  typeFilter: ''
                });
              }}
            >
              Clear All
            </button>
          </div>

          <div className="active-filters-pills">
            {(() => {
              const activeFilterPills = [];

              {/* Date Range Filter */}
              if (filters.dateFrom || filters.dateTo) {
                if (activeFilterPills.length > 0) {
                  activeFilterPills.push(
                    <span key="and-1" className="filter-and-connector"> AND </span>
                  );
                }
                activeFilterPills.push(
                  <div key="date-pill" className="filter-pill">
                    Updated: {filters.dateFrom || '?'} to {filters.dateTo || '?'}
                    <span
                      className="filter-pill-remove"
                      onClick={() => {
                        setFilters({ ...filters, dateFrom: '', dateTo: '' });
                      }}
                    >
                      ✕
                    </span>
                  </div>
                );
              }

              {/* Department Filter */}
              if (filters.departmentFilters.length > 0) {
                if (activeFilterPills.length > 0) {
                  activeFilterPills.push(
                    <span key="and-2" className="filter-and-connector"> AND </span>
                  );
                }
                activeFilterPills.push(
                  <div key="dept-pill" className="filter-pill">
                    Department: {filters.departmentFilters.join(', ')}
                    <span
                      className="filter-pill-remove"
                      onClick={() => setFilters({ ...filters, departmentFilters: [] })}
                    >
                      ✕
                    </span>
                  </div>
                );
              }

              {/* Maintenance Type Filter */}
              if (filters.maintenanceTypeFilters.length > 0) {
                if (activeFilterPills.length > 0) {
                  activeFilterPills.push(
                    <span key="and-3" className="filter-and-connector"> AND </span>
                  );
                }
                activeFilterPills.push(
                  <div key="maint-pill" className="filter-pill">
                    Maintenance Type: {filters.maintenanceTypeFilters.join(', ')}
                    <span
                      className="filter-pill-remove"
                      onClick={() => setFilters({ ...filters, maintenanceTypeFilters: [] })}
                    >
                      ✕
                    </span>
                  </div>
                );
              }

              {/* Type Filter */}
              if (filters.typeFilter) {
                if (activeFilterPills.length > 0) {
                  activeFilterPills.push(
                    <span key="and-4" className="filter-and-connector"> AND </span>
                  );
                }
                const typeLabel = filters.typeFilter === 'maintenance' ? 'Maintenance' : 'Complaint';
                activeFilterPills.push(
                  <div key="type-pill" className="filter-pill">
                    Type: {typeLabel}
                    <span
                      className="filter-pill-remove"
                      onClick={() => setFilters({ ...filters, typeFilter: '' })}
                    >
                      ✕
                    </span>
                  </div>
                );
              }

              {/* Complaint Status Filter */}
              if (filters.complaintStatusFilters.length > 0) {
                if (activeFilterPills.length > 0) {
                  activeFilterPills.push(
                    <span key="and-5" className="filter-and-connector"> AND </span>
                  );
                }
                activeFilterPills.push(
                  <div key="status-pill" className="filter-pill">
                    Status: {filters.complaintStatusFilters.join(', ')}
                    <span
                      className="filter-pill-remove"
                      onClick={() => setFilters({ ...filters, complaintStatusFilters: [] })}
                    >
                      ✕
                    </span>
                  </div>
                );
              }

              {/* Complaint Severity Filter */}
              if (filters.severityFilters.length > 0) {
                if (activeFilterPills.length > 0) {
                  activeFilterPills.push(
                    <span key="and-6" className="filter-and-connector"> AND </span>
                  );
                }
                activeFilterPills.push(
                  <div key="severity-pill" className="filter-pill">
                    Severity: {filters.severityFilters.join(', ')}
                    <span
                      className="filter-pill-remove"
                      onClick={() => setFilters({ ...filters, severityFilters: [] })}
                    >
                      ✕
                    </span>
                  </div>
                );
              }

              return activeFilterPills;
            })()}
          </div>
        </div>
      )}

      {/* REACTDOM PORTAL - Advanced Filters Drawer */}
      {showAdvancedPanel &&
        ReactDOM.createPortal(
          <div className="sidebar-drawer-overlay" onClick={() => setShowAdvancedPanel(false)}>
            <div className="sidebar-drawer" onClick={(e) => e.stopPropagation()}>
              <div className="drawer-title">
                <span>🔧 Advanced Filters</span>
                <button
                  type="button"
                  className="drawer-close-btn"
                  onClick={() => setShowAdvancedPanel(false)}
                  title="Close filters"
                >
                  ✕
                </button>
              </div>

              {/* Warning about mutually exclusive filters */}
              <div style={{
                margin: '0.75rem 1rem',
                padding: '0.6rem 0.8rem',
                background: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: '6px',
                fontSize: '0.8rem',
                color: '#92400e',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem'
              }}>
                <span style={{ fontSize: '1rem', flexShrink: 0 }}>⚠️</span>
                <div>
                  <strong>Choose either Maintenance Type or Complaint filters (Status/Severity).</strong>{' '}
                  {ADVANCED_FILTER_HINTS.mutualExclusivityBanner}
                </div>
              </div>

              <div className="drawer-filter-content">
              {/* DATE RANGE FILTER */}
              <div className="filter-group">
                <div
                  className="filter-group-title"
                  onClick={() => setExpandedSections(prev => ({ ...prev, dateRange: !prev.dateRange }))}
                >
                  <span className={`group-arrow ${expandedSections.dateRange ? 'open' : ''}`}>▼</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>📅</span>
                    <span>Updated Date Range</span>
                  </span>
                </div>
                {expandedSections.dateRange && (
                  <div className="filter-group-content">
                    {/* From Date */}
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">From</div>
                      <input
                        type="date"
                        className="filter-select"
                        style={{ padding: '0.5rem 0.75rem' }}
                        value={drawerFilters.dateFrom || ''}
                        onChange={(e) => setDrawerFilters({ ...drawerFilters, dateFrom: e.target.value })}
                      />
                    </div>

                    {/* To Date */}
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">To</div>
                      <input
                        type="date"
                        className="filter-select"
                        style={{ padding: '0.5rem 0.75rem' }}
                        value={drawerFilters.dateTo || ''}
                        onChange={(e) => setDrawerFilters({ ...drawerFilters, dateTo: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* DEPARTMENT FILTER */}
              <div className="filter-group">
                <div
                  className="filter-group-title"
                  onClick={() => setExpandedSections(prev => ({ ...prev, department: !prev.department }))}
                >
                  <span className={`group-arrow ${expandedSections.department ? 'open' : ''}`}>▼</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>🏢</span>
                    <span>Department</span>
                    {isDepartmentFilterRestricted && (
                      <FilterTitleHint>{ADVANCED_FILTER_HINTS.departmentRestricted}</FilterTitleHint>
                    )}
                  </span>
                </div>
                {expandedSections.department && (
                  <div className="filter-group-content">
                    <div className="filter-item-wrapper full-width">
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.departmentFilters || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          setDrawerFilters({
                            ...drawerFilters,
                            departmentFilters: selected
                          });
                        }}
                        style={{ minHeight: '80px' }}
                        disabled={isDepartmentFilterRestricted}
                        title={isDepartmentFilterRestricted
                          ? 'Only your assigned department is available'
                          : 'Hold Ctrl/Cmd to select multiple departments'}
                      >
                        {/* For department-scoped users (coordinator, custodian, technician): ONLY show their assigned departments */}
                        {/* Managers are location-scoped, so they see all departments like admin/superadmin */}
                        {user && ['coordinator', 'custodian', 'technician'].includes(user.role) ? (
                          user.departments && user.departments.length > 0 ? (
                            user.departments.map((dept) => (
                              <option key={dept.id} value={dept.name}>{dept.icon || '🏢'} {dept.name}</option>
                            ))
                          ) : (
                            <option value="" disabled>No departments assigned</option>
                          )
                        ) : (
                          /* For admin/manager/superadmin/central_help_desk: show all departments from API fetch */
                          availableDepartments
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((dept) => (
                              <option key={dept.id} value={dept.name}>{dept.icon || '🏢'} {dept.name}</option>
                            ))
                        )}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* MAINTENANCE TYPE FILTER */}
              <div className="filter-group" style={{ opacity: drawerHasComplaintFilters ? 0.5 : 1 }}>
                <div
                  className="filter-group-title"
                  onClick={() => setExpandedSections(prev => ({ ...prev, maintenanceType: !prev.maintenanceType }))}
                >
                  <span className={`group-arrow ${expandedSections.maintenanceType ? 'open' : ''}`}>▼</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>🔧</span>
                    <span>Maintenance Type</span>
                    {drawerHasComplaintFilters && (
                      <FilterTitleHint>{ADVANCED_FILTER_HINTS.maintenanceTypeDisabled}</FilterTitleHint>
                    )}
                  </span>
                </div>
                {expandedSections.maintenanceType && (
                  <div className="filter-group-content">
                    <div className="filter-item-wrapper full-width">
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.maintenanceTypeFilters || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          setDrawerFilters({
                            ...drawerFilters,
                            maintenanceTypeFilters: selected
                          });
                        }}
                        style={{ minHeight: '100px' }}
                        title="Hold Ctrl/Cmd to select multiple types"
                        disabled={drawerHasComplaintFilters}
                      >
                        <option value="PPM">PPM</option>
                        <option value="Calibration">Calibration</option>
                        <option value="Compliance">Compliance</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* COMPLAINT FILTERS — Status + Severity side by side */}
              <div className="filter-group" style={{ opacity: drawerHasMaintenanceTypeFilters ? 0.5 : 1 }}>
                <div
                  className="filter-group-title"
                  onClick={() => setExpandedSections(prev => ({ ...prev, complaintFilters: !prev.complaintFilters }))}
                >
                  <span className={`group-arrow ${expandedSections.complaintFilters ? 'open' : ''}`}>▼</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>💬</span>
                    <span>Complaint Filters</span>
                    {drawerHasMaintenanceTypeFilters && (
                      <FilterTitleHint>{ADVANCED_FILTER_HINTS.complaintFiltersDisabled}</FilterTitleHint>
                    )}
                  </span>
                </div>
                {expandedSections.complaintFilters && (
                  <div className="filter-group-content complaint-filters-two-col">
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">Status</div>
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.complaintStatusFilters || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          setDrawerFilters({
                            ...drawerFilters,
                            complaintStatusFilters: selected
                          });
                        }}
                        style={{ minHeight: '80px', width: '100%' }}
                        title="Hold Ctrl/Cmd to select multiple statuses"
                        disabled={drawerHasMaintenanceTypeFilters}
                      >
                        <option value="closed">Closed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">Severity</div>
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.severityFilters || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          setDrawerFilters({
                            ...drawerFilters,
                            severityFilters: selected
                          });
                        }}
                        style={{ minHeight: '80px', width: '100%' }}
                        title="Hold Ctrl/Cmd to select multiple severities"
                        disabled={drawerHasMaintenanceTypeFilters}
                      >
                        <option value="critical">Critical</option>
                        <option value="major">Major</option>
                        <option value="minor">Minor</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
              </div>

              {/* ACTION BUTTONS */}
              <div className="drawer-buttons">
                <button
                  className="cancel-btn"
                  onClick={() => {
                    setShowAdvancedPanel(false);
                  }}
                  title="Close filters without applying changes"
                >
                  Close
                </button>
                <button
                  className="reset-btn"
                  onClick={() => {
                    // Reset drawer filters only
                    setDrawerFilters({
                      dateFrom: '',
                      dateTo: '',
                      departmentFilters: [],
                      typeFilter: '',
                      maintenanceTypeFilters: [],
                      complaintStatusFilters: [],
                      severityFilters: []
                    });
                  }}
                >
                  Reset
                </button>
                <button
                  className="apply-btn"
                  onClick={() => {
                    // Validate Date Range
                    if (drawerFilters.dateFrom && drawerFilters.dateTo) {
                      if (drawerFilters.dateFrom > drawerFilters.dateTo) {
                        alert('From date cannot be greater than To date');
                        return;
                      }
                    }

                    // Enforce mutual exclusivity: maintenance type vs complaint filters
                    const drawerHasComplaintFilters = drawerFilters.complaintStatusFilters.length > 0
                      || drawerFilters.severityFilters.length > 0;
                    if (drawerFilters.maintenanceTypeFilters.length > 0 && drawerHasComplaintFilters) {
                      alert('Cannot apply both Maintenance Type and Complaint filters. Please select only one group.');
                      return;
                    }

                    // Apply drawerFilters to main filters
                    setFilters({
                      ...filters,
                      dateFrom: drawerFilters.dateFrom,
                      dateTo: drawerFilters.dateTo,
                      departmentFilters: drawerFilters.departmentFilters,
                      typeFilter: drawerFilters.typeFilter,
                      maintenanceTypeFilters: drawerFilters.maintenanceTypeFilters,
                      complaintStatusFilters: drawerFilters.complaintStatusFilters,
                      severityFilters: drawerFilters.severityFilters
                    });
                    setShowAdvancedPanel(false);
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      }

      {/* Results */}
      {loading ? (
        <div className="work-logs-loading">
          <div className="spinner"></div>
          <p>Loading maintenance history...</p>
        </div>
      ) : filteredHistory.length === 0 ? (
        <div className="empty-state">
          <p className="empty-icon">📭</p>
          <p className="empty-title">No records found</p>
          <p className="empty-subtitle">Try adjusting your filters or search terms</p>
        </div>
      ) : (
        <>
          <div className="work-logs-results-info">
            Showing {paginatedHistory.length} of {stats.total_count} records
            {stats.total_count > 0 && (
              <span> — Export includes all {stats.total_count} filtered records</span>
            )}
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            paddingBottom: '1rem',
            marginBottom: '1rem',
            borderBottom: '2px solid #e5e7eb'
          }}>
            <ExportButton
              onExportPDF={exportToPDF}
              onExportExcel={exportToExcel}
              title="Export maintenance data"
            />
            <button
              style={{
                background: 'none',
                border: 'none',
                padding: '0.25rem',
                cursor: 'pointer',
                fontSize: '1.5rem',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: '1'
              }}
              onClick={() => setShowPdfColumnsConfig(true)}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.15)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
              title="Configure export columns"
            >
              ⚙️
            </button>
          </div>

          <div className="work-logs-table-wrapper">
            <table className="work-logs-table">
              <thead>
                <tr>
                  <th
                    className="col-type"
                    onClick={() => handleSortChange('type')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by type"
                  >
                    Type{getSortIndicator('type')}
                  </th>
                  <th
                    className="col-title"
                    onClick={() => handleSortChange('title')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by activity"
                  >
                    Title{getSortIndicator('title')}
                  </th>
                  <th
                    className="col-device"
                    onClick={() => handleSortChange('device_name')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by device"
                  >
                    Device{getSortIndicator('device_name')}
                  </th>
                  <th
                    className="col-department"
                    onClick={() => handleSortChange('department')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by department"
                  >
                    Department{getSortIndicator('department')}
                  </th>
                  <th
                    className="col-technician"
                    onClick={() => handleSortChange('performed_by_name')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by technician"
                  >
                    Performed By{getSortIndicator('performed_by_name')}
                  </th>
                  <th
                    className="col-date"
                    onClick={() => handleSortChange('date')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by updated date"
                  >
                    Updated{getSortIndicator('date')}
                  </th>
                  <th
                    className="col-hours"
                    onClick={() => handleSortChange('hours_spent')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Hours spent (hh:mm) — click to sort"
                  >
                    <span className="work-logs-th-stacked">
                      <span className="work-logs-th-label">
                        Hours{getSortIndicator('hours_spent')}
                      </span>
                      <span className="work-logs-th-hint">hh:mm</span>
                    </span>
                  </th>
                  <th className="col-expand" aria-label="Expand details" />
                </tr>
              </thead>
              <tbody>
                {paginatedHistory.map((item) => {
                  const isExpanded = expandedRowId === item.id;
                  return (
                  <React.Fragment key={item.id}>
                    <tr
                      className={`work-log-row work-log-${item.type}${isExpanded ? ' is-expanded' : ''}`}
                      onClick={() => setExpandedRowId(isExpanded ? null : item.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setExpandedRowId(isExpanded ? null : item.id);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? 'Hide details' : 'Show details'}
                      title={isExpanded ? 'Hide details' : 'Show details'}
                    >
                      <td className="col-type">
                        <div className="type-stacked">
                          <span className="type-text">
                            {item.type === 'complaint' ? 'Complaint' : 'Maintenance'}
                          </span>
                          {item.type === 'complaint' ? (
                            <SeverityBadge severity={item.severity} size="small" />
                          ) : (
                            item.maintenance_type && (
                              <span
                                className="maintenance-type-badge"
                                data-type={item.maintenance_type?.toLowerCase()}
                              >
                                {getMaintenanceTypeLabel(item.maintenance_type)}
                              </span>
                            )
                          )}
                        </div>
                      </td>
                      <td className="col-title">
                        <div className="title-content">
                          {item.type === 'complaint' ? (
                            <>
                              <span className="title-text">{item.title}</span>
                              <ComplaintIDBadge complaintId={item.complaint_id} size="small" />
                            </>
                          ) : (
                            <span className="title-text">{item.title}</span>
                          )}
                        </div>
                      </td>
                      <td className="col-device">
                        <div className="device-info">
                          <span className="device-name">{item.device_name}</span>
                          <DeviceIDBadge deviceId={item.device_id} size="small" />
                        </div>
                      </td>
                      <td className="col-department">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '1rem' }}>{item.department_icon || '🏢'}</span>
                          <span>{item.department || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="col-technician">{item.performed_by_name || 'N/A'}</td>
                      <td className="col-date">
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <div style={{ fontWeight: '500', fontSize: '0.9rem', color: '#2c3e50' }}>
                            {formatDateTime(item.date).date}
                          </div>
                          <div style={{ fontWeight: '500', fontSize: '0.8rem', color: '#6c757d' }}>
                            {formatDateTime(item.date).time}
                          </div>
                        </div>
                      </td>
                      <td className="col-hours">
                        <span className="hours-badge">{formatHoursSpent(item.hours_spent)}</span>
                      </td>
                      <td className="col-expand">
                        <span
                          className={`work-log-expand-chevron${isExpanded ? ' is-expanded' : ''}`}
                          aria-hidden="true"
                        />
                      </td>
                    </tr>

                    {/* Expanded Details Row */}
                    {isExpanded && (
                      <tr className="work-log-details-row">
                        <td colSpan="8">
                          <div className="details-container">
                            {item.type === 'complaint' ? (
                              <>
                                <div className="details-body">
                                  <div className="details-field">
                                    <span className="details-label">Details</span>
                                    <ClampedDetailsText text={item.problem_details} />
                                  </div>
                                  {item.closing_remarks && (
                                    <div className="details-field">
                                      <span className="details-label">Resolution</span>
                                      <ClampedDetailsText text={item.closing_remarks} />
                                    </div>
                                  )}
                                </div>
                                <div className="details-facts">
                                  <div className="details-fact">
                                    <span className="details-fact-label">Spare parts</span>
                                    <span className={`details-fact-value ${item.spare_parts_required ? 'is-yes' : 'is-no'}`}>
                                      {item.spare_parts_required ? 'Yes' : 'No'}
                                    </span>
                                  </div>
                                  {item.severity && (
                                    <div className="details-fact">
                                      <span className="details-fact-label">Severity</span>
                                      <SeverityBadge severity={item.severity} size="small" />
                                    </div>
                                  )}
                                  <div className="details-fact">
                                    <span className="details-fact-label">Resolution time</span>
                                    <span className="details-fact-value">
                                      {calculateResolutionTime(item.created_at, item.closed_at)}
                                    </span>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="details-body">
                                  <div className="details-field details-field--full">
                                    <span className="details-label">Work description</span>
                                    <ClampedDetailsText text={item.description || 'No description'} />
                                  </div>
                                </div>
                                {(item.due_date || item.next_due_date) && (
                                  <div className="details-facts">
                                    {item.due_date && (
                                      <div className="details-fact">
                                        <span className="details-fact-label">Due (SLA)</span>
                                        <span className="details-fact-value">{formatDate(item.due_date)}</span>
                                      </div>
                                    )}
                                    {item.due_date && (() => {
                                      const timeliness = getMaintenanceTimeliness(item.maintenance_date, item.due_date);
                                      const statusClass = timeliness.isOnTime === null
                                        ? 'is-na'
                                        : (timeliness.isOnTime ? 'is-on-time' : 'is-late');
                                      const statusText = timeliness.isOnTime === null
                                        ? 'N/A'
                                        : (timeliness.isOnTime
                                          ? 'On-time'
                                          : `${timeliness.daysLate} day${timeliness.daysLate === 1 ? '' : 's'} late`);
                                      return (
                                        <div className="details-fact">
                                          <span className="details-fact-label">Completion</span>
                                          <span className={`details-fact-value ${statusClass}`}>{statusText}</span>
                                        </div>
                                      );
                                    })()}
                                    {item.next_due_date && (
                                      <div className="details-fact">
                                        <span className="details-fact-label">Next due</span>
                                        <span className="details-fact-value">{formatDate(item.next_due_date)}</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                            <div className="details-meta">
                              <span className="meta-item">
                                Logged {formatDateTime(item.logDate).date} · {formatDateTime(item.logDate).time}
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              perPage={perPage}
              onPerPageChange={(newPerPage) => {
                setPerPage(newPerPage);
                setCurrentPage(1);
              }}
              totalItems={totalRecords}
              itemLabel="records"
            />
          )}
          </div>
        </>
      )}

      <FilterHelpModal
        isOpen={showFilterHelp}
        onClose={() => setShowFilterHelp(false)}
        filterType="maintenance"
      />
    </div>
  );
};

export default DeviceMaintenanceHistory;
